import { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  PersonOutlined,
  PaletteOutlined,
  LanguageOutlined,
  LockOutlined,
} from '@mui/icons-material';
import { setMode, setTimeMode, setOpenSidebar } from 'state';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import ScrollToTop from 'components/ScrollToTop';
import ChangePassword from './ChangePassword';
import BiometricSetup from './BiometricSetup';


const SettingRow = ({ label, description, children }) => (
  <Box
    display="grid"
    gridTemplateColumns={{ xs: '1fr auto', md: '1fr 1fr' }}
    alignItems="center"
    gap={2}
    py={2}
  >
    <Box>
      <Typography variant="body1" fontWeight={500}>{label}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">{description}</Typography>
      )}
    </Box>
    <Box>{children}</Box>
  </Box>
);


const Settings = () => {
  const dispatch = useDispatch();
  const theme    = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const scrollRef = useRef(null);

  const user     = useSelector((s) => s.auth.user);
  const mode     = useSelector((s) => s.auth.mode);
  const timemode = useSelector((s) => s.auth.timemode);
  const isSidebarOpen = useSelector((s) => s.auth.isSidebarOpen);

  const [tab, setTab] = useState(0);

  const savePrefs = useCallback(async (patch) => {
    try {
      await api.patch('/crewmembers/me/preferences', patch);
    } catch (err) {
      toast.error('Failed to save preference!');
    }
  }, []);

  const handleToggleTheme = useCallback(async () => {
    dispatch(setMode());
    await savePrefs({ themeMode: mode === 'dark' ? 'light' : 'dark' });
  }, [dispatch, mode, savePrefs]);

  const handleToggleSidebar = useCallback(async () => {
    dispatch(setOpenSidebar(!isSidebarOpen));
    await savePrefs({ isSidebarOpen: !isSidebarOpen });
  }, [dispatch, isSidebarOpen, savePrefs]);

  const handleToggleTimeMode = useCallback(async () => {
    dispatch(setTimeMode());
    await savePrefs({ timeMode: timemode === 'UTC' ? 'LocalBase' : 'UTC' });
  }, [dispatch, timemode, savePrefs]);

  const sectionPadding = { px: 0, py: 1 };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header title="Settings" subtitle="Personalization" />
      </FlexBetween>

      <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap={2}>
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}
          sx={{ gridColumn: 'span 12', overflowY: 'auto' }}
        >
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.alt,
                minHeight: 40,
                '& .MuiTab-root': { minHeight: 40, py: 0.5 },
              }}
            >
              <Tab label="Profile"      icon={<PersonOutlined />}   iconPosition="start" />
              <Tab label="Appearance"   icon={<PaletteOutlined />}  iconPosition="start" />
              <Tab label="Localization" icon={<LanguageOutlined />} iconPosition="start" />
              <Tab label="Security"     icon={<LockOutlined />}     iconPosition="start" />
            </Tabs>

            <Box p={3}>
              {/* ── Tab 0: Profile (read-only) ── */}
              {tab === 0 && (
                <Box {...sectionPadding}>
                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                    {[
                      { label: 'Crew Code', value: user?.crewCode },
                      { label: 'Name',      value: user?.crewName },
                      { label: 'Rank',      value: user?.rank },
                      { label: 'Email',     value: user?.email },
                    ].map(({ label, value }) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body1">{value || '—'}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* ── Tab 1: Appearance ── */}
              {tab === 1 && (
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                  gap={0}
                  {...sectionPadding}
                >
                  <SettingRow
                    label="Theme"
                    description={`Current: ${mode === 'dark' ? 'Dark' : 'Light'} mode`}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mode === 'dark'}
                          onChange={handleToggleTheme}
                          color="primary"
                        />
                      }
                      label={mode === 'dark' ? 'Dark' : 'Light'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </SettingRow>

                  <SettingRow
                    label="Sidebar open by default"
                    description={`Current: ${isSidebarOpen ? 'Open' : 'Close'}`}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!isSidebarOpen}
                          onChange={handleToggleSidebar}
                          color="primary"
                        />
                      }
                      label={isSidebarOpen ? 'Open' : 'Close'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </SettingRow>
                </Box>
              )}

              {/* ── Tab 2: Localization ── */}
              {tab === 2 && (
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                  gap={0}
                  {...sectionPadding}
                >
                  <SettingRow
                    label="Time mode"
                    description={`Current: ${timemode === 'UTC' ? 'UTC' : 'Local Base'}`}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={timemode !== 'UTC'}
                          onChange={handleToggleTimeMode}
                          color="primary"
                        />
                      }
                      label={timemode === 'UTC' ? 'UTC' : 'Local Base'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </SettingRow>
                </Box>
              )}

              {/* ── Tab 3: Security ── */}
              {tab === 3 && (
                <Box {...sectionPadding}>
                  <Box
                    display="flex"
                    flexDirection={isDesktop ? 'row' : 'column'}
                    alignItems={isDesktop ? 'flex-start' : 'stretch'}
                    gap={4}
                  >
                    <Box flex={1} minWidth={0}>
                      <ChangePassword />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <BiometricSetup />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
